// eslint-disable-next-line object-curly-newline
import { getCurrentInstance, reactive, toRefs, watch } from '@vue/composition-api'
import { i18n } from '@/plugins/i18n'

export const isObject = (obj) => typeof obj === 'object' && obj !== null

export const isToday = (date) => {
  const today = new Date()

  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

export const dateInPast = (firstDate, secondDate) => {
  if (firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0)) {
    return true
  }

  return false
}

export const getVuetify = () => {
  const ins = getCurrentInstance()?.proxy

  return ins && ins.$vuetify ? ins.$vuetify : null
}

// Thanks: https://medium.com/better-programming/reactive-vue-routes-with-the-composition-api-18c1abd878d1
export const useRouter = () => {
  const vm = getCurrentInstance().proxy

  const state = reactive({
    route: vm.$route,
  })

  watch(
    () => vm.$route,
    (r) => {
      state.route = r
    },
  )

  return { ...toRefs(state), router: vm.$router }
}

export const formatApiError = (data) => {
  if (data == null) return null
  if (typeof data === 'string') return data
  if (typeof data === 'number' || typeof data === 'boolean') return String(data)

  if (Array.isArray(data)) {
    const parts = data.map((item) => formatApiError(item)).filter(Boolean)
    return parts.length ? parts.join('\n') : null
  }

  if (typeof data === 'object') {
    if (typeof data.string === 'string') return data.string
    if (typeof data.message === 'string') return data.message

    if (typeof data.detail === 'string') return data.detail
    if (Array.isArray(data.detail)) {
      const parts = data.detail.map((item) => formatApiError(item)).filter(Boolean)
      if (parts.length) return parts.join('\n')
    }

    const nonFieldErrors = data.nonFieldErrors || data.non_field_errors
    if (nonFieldErrors) {
      const formatted = formatApiError(nonFieldErrors)
      if (formatted) return formatted
    }

    const parts = []
    Object.values(data).forEach((value) => {
      const formatted = formatApiError(value)
      if (formatted) parts.push(formatted)
    })
    return parts.length ? parts.join('\n') : null
  }

  return String(data)
}

const OCCUPANCY_API_ERROR_KEYS = {
  room_demand_below_occupants: 'roomDemandBelowOccupants',
  room_no_capacity_set: 'roomNoCapacitySet',
  room_capacity_exceeded: 'roomCapacityExceeded',
  room_over_capacity_needs_correction: 'roomOverCapacityNeedsCorrection',
  employee_same_room_overlap: 'employeeSameRoomOverlap',
  employee_different_rooms_overlap: 'employeeDifferentRoomsOverlap',
  occupant_date_start_required: 'accommodationDateStartRequired',
}

const EMPLOYEE_OVERLAP_ERROR_CODES = new Set([
  'employee_same_room_overlap',
  'employee_different_rooms_overlap',
])

const EMPLOYEE_OVERLAP_ERROR_PATTERN =
  /^(employee_same_room_overlap|employee_different_rooms_overlap):([^:]*)$/

const buildEmployeeOverlapNavAction = (employeeId) => {
  if (!employeeId) return null

  return {
    label: i18n.t('viewEmployeeAccommodationAssignments'),
    to: {
      name: 'staff-employee-view',
      params: { id: employeeId },
      query: { tab: 'accommodation' },
    },
  }
}

const translateApiErrorLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return null

  const overlapMatch = trimmed.match(EMPLOYEE_OVERLAP_ERROR_PATTERN)
  if (overlapMatch) {
    const [, code, employeeId] = overlapMatch
    const i18nKey = OCCUPANCY_API_ERROR_KEYS[code]
    return {
      message: i18n.t(i18nKey),
      action: buildEmployeeOverlapNavAction(employeeId),
    }
  }

  const parts = trimmed.split(':')
  const code = parts[0]
  const i18nKey = OCCUPANCY_API_ERROR_KEYS[code]

  if (i18nKey) {
    if (code === 'room_over_capacity_needs_correction' && parts.length >= 3) {
      return { message: i18n.t(i18nKey, { current: parts[1], demand: parts[2] }) }
    }
    if (code === 'room_demand_below_occupants' && parts.length >= 2) {
      return { message: i18n.t(i18nKey, { count: parts[1] }) }
    }
    if (EMPLOYEE_OVERLAP_ERROR_CODES.has(code)) {
      return {
        message: i18n.t(i18nKey),
        action: buildEmployeeOverlapNavAction(parts[1]),
      }
    }
    return { message: i18n.t(i18nKey) }
  }

  if (i18n.te(trimmed)) return { message: i18n.t(trimmed) }

  return { message: trimmed }
}

export const parseApiError = (data) => {
  const raw = formatApiError(data)
  if (!raw) return { message: null, action: null }

  let action = null
  const messages = raw
    .split('\n')
    .map((line) => {
      const result = translateApiErrorLine(line)
      if (!result) return null
      if (result.action && !action) action = result.action
      return result.message
    })
    .filter(Boolean)

  return {
    message: messages.length ? messages.join('\n') : null,
    action,
  }
}

export const translateApiError = (data) => parseApiError(data).message

export const isEmpty = (value) => {
  if (value === null || value === undefined || value === '') {
    return true
  }

  if (Array.isArray(value) && value.length === 0) {
    return true
  }

  return false
}

// ——— Get Initial Text from name ——————— //

export const getInitialName = (fullName) =>
  // eslint-disable-next-line implicit-arrow-linebreak
  fullName
    .split(' ')
    .map((n) => n[0])
    .join('')

// ——— Add Alpha To color ——————— //

export const addAlpha = (color, opacity) => {
  const opacityLocal = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255)

  return color + opacityLocal.toString(16).toUpperCase()
}

// ——— Perfect Scrollbar Scroll to bottom ——————— //

export const psScrollToBottom = (psRef) => {
  const scrollEl = psRef.value.$el || psRef.value
  scrollEl.scrollTop = scrollEl.scrollHeight
}

// ——— Perfect Scrollbar Scroll to bottom ——————— //

export const psScrollToTop = (psRef) => () => {
  const scrollEl = psRef.value.$el || psRef.value
  scrollEl.scrollTop = 0
}

// ————————————————————————————————————
//* ——— Color Manipulations
// ————————————————————————————————————

// Thanks: https://stackoverflow.com/a/5624139/10796681
export const rgbToHex = (r, g, b) => {
  const componentToHex = (c) => {
    const hex = c.toString(16)

    return hex.length === 1 ? `0${hex}` : hex
  }

  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`
}

// Thanks: https://stackoverflow.com/a/5624139/10796681
export const hexToRgb = (hex) => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  // eslint-disable-next-line no-param-reassign
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

  /* eslint-disable indent */
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
  /* eslint-enable */
}
